/**
 * A conferência de um arquivo recebido contra o código registrado da versão emitida (E138.F3,
 * GUS-21, ADR-0094). O arquivo sobe, o servidor calcula o código e compara. A tela mostra o
 * resultado, o código apresentado, a referência usada e o limite: integridade do arquivo, não
 * autoria. Serve ao portal (bloco da CCB certificada) e ao histórico do caso (equipe).
 */
import type { AxiosInstance } from 'axios';
import { useId, useState, type ChangeEvent } from 'react';

import { Badge } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { conferirArquivo, EmissoesError, type ConferenciaDoArquivoData } from '@/lib/emissoes';
import { formatarDataHora } from '@/lib/trilha_format';

const t = ptBR.integridadeDoArquivo;

const VARIANTE = {
  corresponde: 'success',
  diverge: 'danger',
  sem_referencia: 'neutral',
} as const;

function Resultado({ conferencia }: { conferencia: ConferenciaDoArquivoData }): JSX.Element {
  const { resultado, referencia } = conferencia;
  return (
    <div className="mt-2" data-testid="conferencia-resultado">
      <Badge variant={VARIANTE[resultado]} data-testid={`conferencia-${resultado}`}>
        {t.resultado[resultado]}
      </Badge>
      <p className="mt-1 text-body-sm text-fg">{t.explicaResultado[resultado]}</p>
      <p className="mt-1 text-caption text-fg-dim">
        <span>{t.codigoApresentado}: </span>
        <span className="break-all font-mono">{conferencia.codigo_apresentado}</span>
      </p>
      {referencia !== null ? (
        <>
          <p className="mt-1 text-caption text-fg-dim" data-testid="conferencia-referencia">
            {t.referenciaUsada
              .replace('{versao}', String(referencia.versao))
              .replace('{etapa}', t.etapa[referencia.etapa])
              .replace('{data}', formatarDataHora(referencia.emitido_em))}
          </p>
          <p className="text-caption text-fg-dim">
            <span>{t.codigoRegistrado}: </span>
            <span className="break-all font-mono">{referencia.codigo}</span>
          </p>
        </>
      ) : null}
    </div>
  );
}

export interface ConferirArquivoProps {
  client: AxiosInstance;
  caseId: string;
  documentoId: string;
  /** Sem versão, vale a última emitida (o servidor decide e devolve qual usou). */
  versao?: number;
}

export function ConferirArquivo({
  client,
  caseId,
  documentoId,
  versao,
}: ConferirArquivoProps): JSX.Element {
  const inputId = useId();
  const [conferindo, setConferindo] = useState(false);
  const [conferencia, setConferencia] = useState<ConferenciaDoArquivoData | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  async function aoEscolher(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const arquivo = event.target.files?.[0];
    // Limpa a escolha: o mesmo arquivo, escolhido de novo, confere de novo.
    event.target.value = '';
    if (arquivo === undefined) return;
    setConferindo(true);
    setErro(null);
    setConferencia(null);
    try {
      setConferencia(await conferirArquivo(client, caseId, documentoId, arquivo, versao));
    } catch (err) {
      setErro(t.erro[err instanceof EmissoesError ? err.motivo : 'indisponivel']);
    } finally {
      setConferindo(false);
    }
  }

  return (
    <div className="mt-2" data-testid={`conferir-arquivo-${documentoId}`}>
      <label htmlFor={inputId} className="block text-caption font-semibold text-fg">
        {t.conferir}
      </label>
      <input
        id={inputId}
        type="file"
        disabled={conferindo}
        onChange={(event) => void aoEscolher(event)}
        data-testid={`conferir-arquivo-input-${documentoId}`}
        className="mt-1 block text-caption text-fg file:mr-2 file:border file:border-border file:bg-surface file:px-2.5 file:py-1 file:text-caption file:text-fg"
      />
      <p className="mt-1 text-caption text-fg-dim">{t.conferirExplica}</p>
      {conferindo ? (
        <p role="status" className="mt-1 text-caption text-fg-dim">
          {t.conferindo}
        </p>
      ) : null}
      {erro !== null ? (
        <p role="alert" className="mt-1 text-caption text-danger-700">
          {erro}
        </p>
      ) : null}
      {conferencia !== null ? <Resultado conferencia={conferencia} /> : null}
      <p className="mt-2 text-caption text-fg-muted">{t.limite}</p>
    </div>
  );
}
